
import { ActionReducerMapBuilder, createAction } from '@reduxjs/toolkit'
import { format } from 'date-fns'
import { firstValueFrom } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { Prompt, sortByKeyAscending } from 'shared'
import { createAppAsyncThunk, StoreThunkDispatch } from 'store/state'
import { aiWriterSelector } from '../selectors'
import ComposerState from '../state'
import { generateImage } from '../suggest/actions'
import * as api from './net'

function promptsAdapter(response: any): Prompt[] {
  const today = new Date().toISOString()

  return response
    .map((item: any, index: number) => {
      const date = item.dcFormatted || item.dc || today
      const createdAt = new Date(date).getTime()
      const response: string[] = []
      const images: string[] = []
      const text = item.response || item.text
      const isImage = item.feature === 'images'
      try {
        if (isImage) {
          images.push(...JSON.parse(item.response))
        } else {
          response.push(...JSON.parse(item.response))
        }
      } catch (_e) {
        if (!isImage) {
          response.push(text)
        }
      }

      return ({
        id: item.id.toString(),
        originalPromptId: item.promptId?.toString() || item.id.toString(),
        text,
        prompt: item.text,
        categoryId: item.categoryId?.toString(),
        order: index,
        createdAt,
        response,
        images,
        isImage,
        imageUrl: item.imageUrl,
        dateFormatted: format(createdAt, 'EEEE, LLLL do')
      })
    })
}

const setUserPrompts = createAction<Prompt[]>('composer/assistant/setUserPrompts')
const userPromptDeleted = createAction<string>('composer/assistant/userPromptDeleted')
const setFavotitePrompts = createAction<Prompt[]>('composer/assistant/setFavoritePrompts')
const addedFavoritePrompt = createAction<Prompt>('composer/assistant/addedFavoritePrompt')
const removedFavoritePrompt = createAction<string>('composer/assistant/removedFavoritePrompt')

export const assistantExtraReducers = (builder: ActionReducerMapBuilder<ComposerState>) => {
  builder
    .addCase(setUserPrompts, (state, action) => {
      state.writer.userPrompts = action.payload
    })
    .addCase(userPromptDeleted, (state, action) => {
      state.writer.userPrompts = state.writer.userPrompts.filter(prompt => prompt.id !== action.payload)
    })
    .addCase(setFavotitePrompts, (state, action) => {
      state.writer.favoritePrompts = action.payload
    })
    .addCase(addedFavoritePrompt, (state, action) => {
      state.writer.favoritePrompts.push(action.payload)
    })
    .addCase(removedFavoritePrompt, (state, action) => {
      state.writer.favoritePrompts = state.writer.favoritePrompts.filter(prompt => prompt.id !== action.payload)
    })
    .addCase(fetchAiImageRequestsLimit.fulfilled, (state, action) => {
      state.writer.imageRequests = action.payload.imageRequests
      state.writer.imageRequestsLimit = action.payload.imageRequestsLimit
    })
    .addCase(generateImage.fulfilled, (state) => {
      state.writer.imageRequests = (state.writer.imageRequests || 0) + 1
    })
}

export function getPromptCategories(admin?: boolean) {
  return (dispatch: StoreThunkDispatch) => dispatch(api.getPromptCategories(admin)).pipe(map(response => {
    return response.map((item: any) => ({
      ...item,
      id: item.id.toString(),
      isVisible: item.isVisible === '1',
      order: parseInt(item.order, 10)
    })).sort(sortByKeyAscending('order'))
  }))
}

export function generateCaptions(text: string, promptId?: string, variations?: number, imageUrl?: string, getImage?: boolean) {
  return (dispatch: StoreThunkDispatch) => dispatch(api.getCaptionSuggestions(text, promptId, variations, imageUrl, getImage))
}

export function getUserPrompts() {
  return (dispatch: StoreThunkDispatch) => dispatch(api.getUserPrompts()).pipe(tap(response => {
    dispatch(setUserPrompts(promptsAdapter(response)))
  }))
}

export function deleteUserPrompt(id: string) {
  return (dispatch: StoreThunkDispatch) => dispatch(api.deleteUserPrompt(id)).pipe(tap(() => {
    dispatch(userPromptDeleted(id))
  }))
}

export function getFavoritePrompts() {
  return (dispatch: StoreThunkDispatch) => dispatch(api.getFavoritePrompts()).pipe(tap(response => {
    const p = response.filter((item: any) => Boolean(item.text))
    dispatch(setFavotitePrompts(promptsAdapter(p)))
  }))
}

export function addFavoritePrompt(prompt: Prompt | { text: string, imageUrl?: string }) {
  return (dispatch: StoreThunkDispatch) => {
    const id = (prompt as Prompt).id
    return dispatch(api.addFavoritePrompt(id, id ? undefined : prompt.text, id ? undefined : (prompt as any).imageUrl))
      .pipe(tap((response) => {
        dispatch(addedFavoritePrompt(id ? prompt as Prompt : promptsAdapter([response])[0]))
      }))
  }
}

export function removeFavoritePrompt(id: string) {
  return (dispatch: StoreThunkDispatch) => dispatch(api.removeFavoritePrompt(id))
    .pipe(tap(() => {
      dispatch(removedFavoritePrompt(id))
    }))
}

export function getPrompts(categoryId?: string, admin?: boolean) {
  return (dispatch: StoreThunkDispatch) => dispatch(api.getPrompts(categoryId, admin)).pipe(map(response => {
    return response.map((item: any) => ({
      ...item,
      id: item.id.toString(),
      categoryId: item.categoryId?.toString(),
      order: parseInt(item.order, 10)
    })).sort(sortByKeyAscending('order'))
  }))
}

export const fetchAiImageRequestsLimit = createAppAsyncThunk(
  'composer/fetchAiImageRequestsLimit',
  async (force: boolean = true, { dispatch, getState, rejectWithValue }) => {
    if (!force) {
      const cached = aiWriterSelector(getState())
      const { imageRequests, imageRequestsLimit } = cached
      return { imageRequests, imageRequestsLimit }
    }
    try {
      const response = await firstValueFrom(dispatch(api.getAssistantLimits()))
      return {
        imageRequests: response.images,
        imageRequestsLimit: response.maxImages
      }
    } catch (e) {
      console.log(e)
      return rejectWithValue(e)
    }
  }
)
